/**
 * Routes ouvertes sans session : lien reçu par email, formulaire embarqué,
 * questionnaire adressé à un client.
 *
 * Extrait des gardes de navigation pour que la réaction à une session expirée
 * (`useSessionExpiryRedirect`) lise la MÊME liste : sur ces pages, un visiteur
 * n'a jamais été connecté, et l'envoyer vers l'écran de connexion du
 * back-office lui ferait quitter la page pour un écran qui ne le concerne pas.
 */

import { estCheminLienFichierConciergerie } from '../utils/conciergerie-liens.js'

/**
 * LA PAGE INTROUVABLE (route attrape-tout, fin de `routes.js`).
 *
 * Elle n'a pas d'adresse à inscrire dans les listes ci-dessous : TOUTE adresse
 * inconnue y mène. Elle se reconnaît donc à son nom de route. Pour un visiteur
 * sans session, elle est un écran public (ni habillage du back-office, ni appel
 * authentifié) ; pour un utilisateur connecté, elle reste dans son cadre.
 */
export const NOT_FOUND_ROUTE_NAME = 'page-introuvable'

/** Vraie quand la route résolue est la page introuvable. */
export function isNotFoundRoute(route) {
  return route?.name === NOT_FOUND_ROUTE_NAME
}

/** Routes publiques du back-office (liens reçus par email). */
export const PUBLIC_ROUTES = [
  '/calendar', '/appointment', '/sign-quote', '/unsubscribe',
  '/inscription', '/inscription/success', '/inscription/payer', '/prestataire',
  '/emargement', '/emargement-formateur', '/formateur-facture',
  '/inscription-formateur', '/questionnaire-formation',
  // Renouvellement annuel : le formateur signe depuis le lien de son email,
  // sans compte ni session.
  '/contrat-annuel',
  // Désinscription depuis le rappel mensuel d'inscription, sans compte.
  '/desinscription-formateur'
]

/**
 * Pages publiques qui ont des SOUS-CHEMINS. La comparaison exacte les manquait
 * une par une, et le prix était lourd : `/inscription-formateur/verifier-email`
 * — la page qui accueille le formateur cliquant « Confirmer mon adresse email »
 * — n'était pas reconnue comme publique. Un 401 reçu au chargement (le bandeau
 * de version demande la préférence d'interface, qui n'existe pas sans session)
 * la faisait donc passer pour une session expirée, et le visiteur atterrissait
 * sur l'écran de connexion du BACK-OFFICE, à qui on demandait un identifiant et
 * un mot de passe qu'il n'avait pas encore reçus.
 *
 * Ce fichier est désormais la SEULE liste : `useAppRouting`, qui en tenait une
 * seconde de son côté, consomme celle-ci. Deux inventaires de pages publiques
 * qu'on doit penser à mettre à jour ensemble sont exactement ce qui a produit
 * le bug — il n'en reste qu'un.
 */
export const PUBLIC_ROUTE_PREFIXES = [
  '/inscription-formateur/',
  '/inscription/',
  '/verifier-attestation/',
]

/**
 * Les trois espaces de portail. Ce ne sont pas des pages « ouvertes aux
 * visiteurs » — elles exigent une session de portail — mais elles n'affichent
 * jamais l'habillage du back-office, et leur 401 renvoie vers LEUR écran de
 * connexion, pas vers /login (cf. useSessionExpiryRedirect).
 */
export const PORTAL_PREFIXES = [
  '/espace-formateur',
  '/espace-entreprise',
  '/espace-stagiaire',
]

/** Pages de portail ouvertes sans session de portail. */
export const PORTAL_PUBLIC_ROUTES = [
  '/mission-reponse',
  '/questionnaire-organisation-public',
  '/questionnaire-organisation/liste-stagiaires'
]

/**
 * Le chemin seul, comparable : sans query, sans fragment, et sans la barre
 * oblique finale.
 *
 * `vue-router` sert `/calendar/` comme `/calendar` — les listes de ce fichier,
 * elles, comparaient à l'identique. Une adresse publique recopiée avec un « / »
 * final passait donc pour une page protégée, et son visiteur partait vers
 * l'écran de connexion du back-office.
 */
function cheminSeul(path) {
  const [sansFragment] = String(path ?? '').split('#')
  const [pathname] = sansFragment.split('?')
  return pathname.length > 1 && pathname.endsWith('/')
    ? pathname.replace(/\/+$/, '')
    : pathname
}

/**
 * Le préfixe du portail dont relève un chemin, ou `null`.
 *
 * Le préfixe compte SEUL (`/espace-formateur`) ou suivi d'une barre — la règle
 * de `resolvePortail` (usePageIntrouvable), désormais partagée. Un `startsWith`
 * nu rangeait `/espace-formateurXYZ` — une adresse inconnue s'il en est — parmi
 * les pages du portail formateur.
 */
export function portalPrefixOf(path) {
  const pathname = cheminSeul(path)
  return PORTAL_PREFIXES.find(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  ) || null
}

/** Vraie pour une page de l'un des trois espaces de portail. */
export function isPortalPath(path) {
  return portalPrefixOf(path) !== null
}

export function isPublicRoute(path) {
  const pathname = cheminSeul(path)
  return PUBLIC_ROUTES.includes(pathname)
    || PUBLIC_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))
    // L'identifiant n'est pas filtré ici. La page appelle une route publique à
    // jeton, qui refuse elle-même un devis inconnu (`JETON_INVALIDE`) et
    // affiche l'issue « lien mort ». Avec `\d+`, une adresse retouchée à la
    // main (`/quotes/abc/…`) n'était plus reconnue comme publique et partait
    // vers /login — or un écran public n'envoie jamais vers la connexion (P14).
    || Boolean(pathname.match(/^\/quotes\/[^/]+\/formation-programme-pdf$/))
    // Le permalien d'un fichier de conciergerie : un formateur l'ouvre sans
    // session du back-office, et la page elle-même demande le fichier au serveur.
    || estCheminLienFichierConciergerie(pathname)
}

/**
 * Vraie pour toute page atteignable sans session, portails compris.
 * `path` peut porter une query (`fullPath`) : seule la partie chemin compte.
 */
export function isOpenToVisitors(path) {
  const pathname = cheminSeul(path)
  return Boolean(isPublicRoute(pathname)) ||
    PORTAL_PUBLIC_ROUTES.includes(pathname) ||
    pathname.startsWith('/embed')
}
